import React from 'react';
import { Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography } from '@mui/material';
import Loading from 'modules/common/components/loading';
import ManagementEmptyResults from 'modules/common/components/managementEmptyResults';
import { Shortcut, ShortcutAction, ShortcutGroup, ShortcutGroupAction, ShortcutGroupToShortcuts } from '../../models';
import { ShortcutsTableListHeader } from './shortcutsTableListHeader';
import { ShortcutsGroupRow } from './shortcutsGroupRow';

interface IShortcutsListProps {
    filteredShortcutFormat: ShortcutGroupToShortcuts[];
    selectedShortcuts: Shortcut[];
    selectedShortcutGroups: ShortcutGroup[];
    onChangeSelectedShortcuts: (shortcuts: Shortcut[]) => void;
    onChangeSelectedShortcutGroups: (shortcutGroups: ShortcutGroup[]) => void;
    onShortcutAction: (shortcut: Shortcut, shortcutAction: ShortcutAction) => void;
    onShortcutGroupAction: (shortcutGroup: ShortcutGroup, shortcutGroupAction: ShortcutGroupAction) => void;
    isFetching: boolean;
    isUpdating: boolean;
    expandedShortcutGroups: string[];
    setExpandedShortcutGroups: React.Dispatch<React.SetStateAction<string[]>>;
    hasFiltersApplied: boolean;
}

export const ShortcutsList: React.FC<IShortcutsListProps> = ({
    filteredShortcutFormat,
    selectedShortcuts,
    selectedShortcutGroups,
    onChangeSelectedShortcuts,
    onChangeSelectedShortcutGroups,
    onShortcutAction,
    onShortcutGroupAction,
    isFetching,
    isUpdating,
    expandedShortcutGroups,
    setExpandedShortcutGroups,
    hasFiltersApplied
}) => {
    const getTableHeader = (): JSX.Element => 
        <TableRow>
            <TableCell padding="checkbox" sx={{ minWidth: "53px" }}/>
            <ShortcutsTableListHeader/>
        </TableRow>;

    const onToggleExpand = (expanded: boolean, group: ShortcutGroup) => 
        expanded 
            ? setExpandedShortcutGroups(prev => [...prev, group.id])
            : setExpandedShortcutGroups(prev => [...prev.filter(esgId => esgId !== group.id)]);

    const onSelectShortcut = (selected: boolean, shortcut: Shortcut) => 
        onChangeSelectedShortcuts(
            selected
                ? [...selectedShortcuts, shortcut] 
                : [...selectedShortcuts.filter(s => s.id !== shortcut.id)]
        );

    const onSelectShortcutGroup = (selected: boolean, shortcutGroup: ShortcutGroup) => {
        onChangeSelectedShortcutGroups(
            selected
                ? [...selectedShortcutGroups, shortcutGroup]
                : [...selectedShortcutGroups.filter(sg => sg.id !== shortcutGroup.id)]
        );

        const shortcutsFromShortcutGroup = filteredShortcutFormat.find(sgts => sgts.shortcutGroup.id === shortcutGroup.id)?.shortcuts ?? [];

        if (selected) {
            onChangeSelectedShortcuts([...selectedShortcuts, ...shortcutsFromShortcutGroup.filter(sfsg => !selectedShortcuts.map(s => s.id).includes(sfsg.id))]);
        }
        else {
            onChangeSelectedShortcuts([...selectedShortcuts.filter(s => !shortcutsFromShortcutGroup.map(sfsg => sfsg.id).includes(s.id))]);
        }
    }

    const getTableBody = (): JSX.Element => {
        const isExpanded = (groupId: string) => {
            return expandedShortcutGroups.findIndex((gId: string) => gId === groupId) !== -1;
        };

        return <React.Fragment>
            {
                filteredShortcutFormat
                    .map(f => 
                        <ShortcutsGroupRow
                            shortcutGroupToShortcuts={f}
                            selectedShortcuts={selectedShortcuts}
                            selectedShortcutGroups={selectedShortcutGroups}
                            onSelectShortcut={onSelectShortcut}
                            onSelectGroup={onSelectShortcutGroup}
                            expanded={isExpanded(f.shortcutGroup.id)}
                            onToggleExpand={onToggleExpand}
                            onShortcutAction={onShortcutAction}
                            onShortcutGroupAction={onShortcutGroupAction}
                            key={f.shortcutGroup.id}
                        />
                )
            }
        </React.Fragment>;
    };

    const getList = (): JSX.Element =>
        <>
            {
                hasFiltersApplied &&
                <Typography variant="h2" className="filter-results">Results</Typography>
            }
            <TableContainer sx={{ overflowX: "clip" }}>
                <Table stickyHeader size="medium" className='shortcuts-main-table'>
                    <TableHead>
                        {getTableHeader()}
                    </TableHead>
                    <TableBody>
                        {getTableBody()}
                    </TableBody>
                </Table>
            </TableContainer>
        </>;

    const getTableContents = (): JSX.Element => {
        if (isFetching || isUpdating) {
            return <Loading />;
        }
        else if (filteredShortcutFormat.length === 0) {
            return <React.Fragment>
                {getList()}
                {<ManagementEmptyResults searchType="Shortcuts" hasFilters={false} />}
            </React.Fragment>;
        }
        else {
            return getList();
        }
    };

    return getTableContents();
};